/**
 * Theme JS — cart drawer, search overlay, mobile nav, variant picker helpers
 */
(function () {
  'use strict';

  const moneyFormat = window.themeSettings?.moneyFormat || '{{amount}}';

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    const amount = (Number(cents) / 100).toFixed(2);
    return '₹' + amount;
  }

  /* ---------- Mobile nav ---------- */
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  const menuClose = document.querySelector('[data-menu-close]');
  const menuBackdrop = document.querySelector('[data-mobile-nav-backdrop]');

  function openMenu() {
    if (!mobileNav) return;
    mobileNav.hidden = false;
    mobileNav.setAttribute('open', '');
    menuBackdrop?.removeAttribute('hidden');
    menuToggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeMenu() {
    if (!mobileNav) return;
    mobileNav.removeAttribute('open');
    menuBackdrop?.setAttribute('hidden', '');
    menuToggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
  }

  menuToggle?.addEventListener('click', openMenu);
  menuClose?.addEventListener('click', closeMenu);
  menuBackdrop?.addEventListener('click', closeMenu);
  mobileNav?.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  });
  window.addEventListener('resize', () => {
    if (window.matchMedia('(min-width: 990px)').matches) closeMenu();
  });

  /* ---------- Search overlay ---------- */
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchClose = document.querySelector('[data-search-close]');
  const searchInput = document.querySelector('[data-predictive-input]');
  const searchResults = document.querySelector('[data-predictive-results]');
  let searchTimer;

  function openSearch() {
    searchOverlay?.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => searchInput?.focus(), 50);
  }

  function closeSearch() {
    searchOverlay?.removeAttribute('open');
    document.documentElement.style.overflow = '';
  }

  searchToggle?.addEventListener('click', openSearch);
  searchClose?.addEventListener('click', closeSearch);

  searchInput?.addEventListener('input', (e) => {
    clearTimeout(searchTimer);
    const q = e.target.value.trim();
    if (q.length < 2) {
      if (searchResults) searchResults.innerHTML = '';
      return;
    }
    searchTimer = setTimeout(() => fetchPredictive(q), 250);
  });

  async function fetchPredictive(q) {
    try {
      const res = await fetch(
        `${window.themeRoutes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`
      );
      // Fallback to JSON API if section not available
      const jsonRes = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`
      );
      const data = await jsonRes.json();
      const products = data.resources?.results?.products || [];
      if (!searchResults) return;
      if (!products.length) {
        searchResults.innerHTML = `<p class="search-overlay__empty">No results for “${q}”</p>`;
        return;
      }
      searchResults.innerHTML = products
        .map(
          (p) => `
        <a class="search-overlay__item" href="${p.url}">
          ${p.image ? `<img src="${p.image}" alt="" width="56" height="72" loading="lazy">` : '<span class="search-overlay__thumb"></span>'}
          <span>
            <strong>${p.title}</strong>
            <span class="search-overlay__price">${p.price}</span>
          </span>
        </a>`
        )
        .join('');
    } catch (err) {
      console.warn('Predictive search error', err);
    }
  }

  /* ---------- Cart drawer ---------- */
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartOpenButtons = document.querySelectorAll('[data-cart-open]');
  const cartClose = document.querySelector('[data-cart-drawer-close]');

  function openCart() {
    if (!cartDrawer) return;
    if (typeof cartDrawer.showModal === 'function') {
      if (!cartDrawer.open) cartDrawer.showModal();
    } else {
      cartDrawer.setAttribute('open', '');
    }
  }

  function closeCart() {
    if (!cartDrawer) return;
    if (typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    } else {
      cartDrawer.removeAttribute('open');
    }
  }

  cartOpenButtons.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      if (window.themeSettings?.cartDrawerEnabled === false) return;
      e.preventDefault();
      openCart();
    });
  });
  cartClose?.addEventListener('click', closeCart);
  cartDrawer?.addEventListener('click', (e) => {
    if (e.target === cartDrawer) closeCart();
  });

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? String(count) : '';
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      el.textContent = count > 0 ? `(${count})` : '';
    });
  }

  function updateShippingBar(totalPrice) {
    const wrap = document.querySelector('[data-free-shipping]');
    if (!wrap) return;
    const threshold = Number(wrap.dataset.threshold || 0);
    if (!threshold) return;
    const remaining = Math.max(threshold - totalPrice, 0);
    const progress = Math.min((totalPrice / threshold) * 100, 100);
    const text = wrap.querySelector('[data-shipping-text]');
    const bar = wrap.querySelector('[data-shipping-bar]');
    if (bar) bar.style.width = `${progress}%`;
    if (text) {
      text.textContent =
        remaining === 0
          ? window.themeStrings?.freeShippingUnlocked || "You've unlocked free shipping!"
          : (window.themeStrings?.freeShippingRemaining || 'Add {{ amount }} more for free shipping').replace(
              '{{ amount }}',
              formatMoney(remaining)
            );
    }
  }

  async function refreshCartDrawer() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      updateCartCount(cart.item_count);
      updateShippingBar(cart.total_price);
      const subtotal = document.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

      const body = document.querySelector('[data-cart-drawer-body]');
      if (!body) return;

      if (cart.item_count === 0) {
        body.innerHTML = `<div class="cart-drawer__empty"><p>${window.themeStrings?.cartEmpty || 'Your bag is empty'}</p></div>`;
        const footer = cartDrawer?.querySelector('[data-cart-footer]');
        if (footer) footer.hidden = true;
        return;
      }

      const itemsHtml = cart.items
        .map(
          (item) => `
        <li class="cart-drawer__item" data-key="${item.key}">
          <a href="${item.url}" class="cart-drawer__item-image">
            ${item.image ? `<img src="${item.image}" alt="" loading="lazy" width="80" height="100">` : ''}
          </a>
          <div class="cart-drawer__item-details">
            <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
            ${item.variant_title ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
            <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
            <div class="cart-drawer__item-actions">
              <div class="qty-stepper" data-qty-stepper>
                <button type="button" data-qty-change="-1" aria-label="Decrease">−</button>
                <input type="number" value="${item.quantity}" min="0" data-qty-input data-key="${item.key}">
                <button type="button" data-qty-change="1" aria-label="Increase">+</button>
              </div>
              <button type="button" class="cart-drawer__remove" data-remove-item data-key="${item.key}">Remove</button>
            </div>
          </div>
        </li>`
        )
        .join('');

      body.innerHTML = `<ul class="cart-drawer__items" data-cart-items>${itemsHtml}</ul>`;
      const footer = cartDrawer?.querySelector('[data-cart-footer]');
      if (footer) footer.hidden = false;
      bindCartItemEvents();
    } catch (err) {
      console.warn('Cart refresh failed', err);
    }
  }

  async function changeCart(key, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    await refreshCartDrawer();
  }

  function bindCartItemEvents() {
    cartDrawer?.querySelectorAll('[data-qty-change]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const stepper = btn.closest('[data-qty-stepper]');
        const input = stepper?.querySelector('[data-qty-input]');
        if (!input) return;
        const delta = Number(btn.dataset.qtyChange);
        const next = Math.max(0, Number(input.value) + delta);
        input.value = next;
        changeCart(input.dataset.key, next);
      });
    });

    cartDrawer?.querySelectorAll('[data-remove-item]').forEach((btn) => {
      btn.addEventListener('click', () => changeCart(btn.dataset.key, 0));
    });
  }

  bindCartItemEvents();

  /* ---------- Add to cart forms ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;
    if (window.themeSettings?.cartDrawerEnabled === false) return;

    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await refreshCartDrawer();
      openCart();
    } catch (err) {
      console.warn(err);
      form.submit();
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  /* ---------- Variant picker ---------- */
  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    const select = picker.querySelector('[data-variant-select]');
    const jsonEl = picker.parentElement?.querySelector('[data-variant-json]') || document.querySelector('[data-variant-json]');
    let variants = [];
    try {
      variants = JSON.parse(jsonEl?.textContent || '[]');
    } catch (_) {}

    picker.querySelectorAll('[data-option-value]').forEach((input) => {
      input.addEventListener('change', () => {
        const selected = [];
        picker.querySelectorAll('fieldset').forEach((fs, i) => {
          const checked = fs.querySelector('input:checked');
          selected[i] = checked ? checked.value : null;
          const label = fs.querySelector('[data-selected-value]');
          if (label && checked) label.textContent = checked.value;
        });

        const match = variants.find(
          (v) =>
            (selected[0] == null || v.option1 === selected[0]) &&
            (selected[1] == null || v.option2 === selected[1]) &&
            (selected[2] == null || v.option3 === selected[2])
        );

        if (match && select) {
          select.value = match.id;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        document.querySelectorAll('[data-product-price]').forEach((el) => {
          if (!match) return;
          el.innerHTML = match.compare_at_price > match.price
            ? `<span class="price price--on-sale"><span class="price__compare">${formatMoney(match.compare_at_price)}</span><span class="price__regular">${formatMoney(match.price)}</span></span>`
            : `<span class="price"><span class="price__regular">${formatMoney(match.price)}</span></span>`;
        });

        const atc = document.querySelector('[data-add-to-cart]');
        if (atc && match) {
          atc.disabled = !match.available;
          atc.textContent = match.available
            ? window.themeStrings?.addToCart || 'Add to bag'
            : window.themeStrings?.soldOut || 'Sold out';
        }

        const stickyPrice = document.querySelector('[data-sticky-price]');
        if (stickyPrice && match) stickyPrice.textContent = formatMoney(match.price);
      });
    });
  });

  /* ---------- Size guide ---------- */
  document.querySelectorAll('[data-size-guide-open]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const dialog = document.querySelector('[data-size-guide]');
      if (dialog?.showModal) dialog.showModal();
      else dialog?.setAttribute('open', '');
    });
  });
  document.querySelectorAll('[data-size-guide-close]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const dialog = document.querySelector('[data-size-guide]');
      if (dialog?.close) dialog.close();
      else dialog?.removeAttribute('open');
    });
  });

  /* ---------- Product gallery ---------- */
  const thumbs = document.querySelectorAll('[data-gallery-thumb]');
  const mainImage = document.querySelector('[data-gallery-main] img');
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.fullSrc;
      const srcset = thumb.dataset.fullSrcset;
      if (mainImage && src) {
        mainImage.src = src;
        if (srcset) mainImage.srcset = srcset;
      }
      thumbs.forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  window.themeCart = { open: openCart, close: closeCart, refresh: refreshCartDrawer };
})();
